import React, { useState, useEffect } from "react";
import axios from "axios";
import "../../Styles/AdminAttendanceHistory.css";
import Loader from "../../Components/Loader";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import html2pdf from "html2pdf.js";
import * as XLSX from "xlsx";
import { Link } from "react-router-dom";

function AdminAttendanceHistory() {
  const [selectedTab, setSelectedTab] = useState("faculty");
  const currentMonth = new Date().toLocaleString("default", { month: "long" });
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [searchTerm, setSearchTerm] = useState("");
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [exportChoice, setExportChoice] = useState("");

  const currentYear = new Date().getFullYear();

  const getDaysInMonth = (month, year) => {
    const monthIndex = new Date(Date.parse(month + " 1, " + year)).getMonth();
    return new Date(year, monthIndex + 1, 0).getDate();
  };

  useEffect(() => {
    const fetchAttendanceHistory = async () => {
      setLoading(true);
      setError(null);
      setAttendanceData([]);
      try {
        const token = localStorage.getItem("authToken");
        if (!token) return;

        const apiUrl =
          selectedTab === "faculty"
            ? "https://api.uniums.com/api/admin/faculty-attendance-history"
            : "https://api.uniums.com/api/admin/student-attendance-history";

        const monthIndex =
          new Date(selectedMonth + " 1, " + currentYear).getMonth() + 1;
        const response = await axios.get(apiUrl, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            month: monthIndex,
            year: currentYear,
          },
        });

        if (response.data && response.data.data.length > 0) {
          setAttendanceData(response.data.data);
          toast.success("Attendance data loaded successfully!");
        } else {
          toast.warning("No attendance data found!");
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Error fetching attendance history.");
        toast.error("Failed to load attendance data!");
      } finally {
        setLoading(false);
      }
    };

    fetchAttendanceHistory();
  }, [selectedMonth, selectedTab, currentYear]);

  const handleExportPDF = () => {
    const element = document.getElementById("attendance-table"); 

    const options = {
      margin: 0.5, 
      filename: "attendance-history.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2, 
        useCORS: true, 
        x: 0,
        y: 0,
        width: element.offsetWidth, 
        height: element.scrollHeight, 
        scrollX: 0,
        scrollY: -window.scrollY,
      },
      jsPDF: {
        unit: "in",
        format: "a4", 
        orientation: "landscape", 
        compress: true,
        autoPaging: true, 
        maxWidth: element.offsetWidth, 
        pageBreak: "auto",
      },
    };
    html2pdf().from(element).set(options).save();
  };

  // Excel Export Function
  const handleExportExcel = () => {
    const ws = XLSX.utils.table_to_sheet(
      document.getElementById("attendance-table")
    ); 
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance History"); 
    XLSX.writeFile(wb, "attendance-history.xlsx");
  };

  const filteredData = attendanceData.filter(
    (person) =>
      person.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      "" ||
      person.userCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ""
  );

  const daysInMonth = getDaysInMonth(selectedMonth, currentYear);

  const renderAttendanceCell = (status) => {
    switch (status) {
      case "Present":
        return <span>Present</span>;
      case "Absent":
        return <span>Absent</span>;
      default:
        return "-";
    }
  };

  return (
    <div className="AdminAttendanceHistory">
      <ToastContainer />
      <h1>Attendance History</h1>

      <div className="view_more_link">
        <Link to={"/attendance-view-more-details"}>View More Details</Link>
      </div>
      <div className="search-bar">
        <input
          type="text"
          className="search-box"
          placeholder="Search by name or code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div className="attendance-header">
        <div className="left-controls">
          <select
            onChange={(e) => setSelectedTab(e.target.value)}
            value={selectedTab}
          >
            <option value="faculty">Faculty</option>
            <option value="student">Student</option>
          </select>
          <select
            onChange={(e) => setSelectedMonth(e.target.value)}
            value={selectedMonth}
          >
            {[...Array(12)].map((_, i) => (
              <option
                key={i}
                value={new Date(0, i).toLocaleString("default", {
                  month: "long",
                })}
              >
                {new Date(0, i).toLocaleString("default", { month: "long" })}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={currentYear}
            disabled
            className="year-field"
          />
        </div>
        <div className="right-controls">
          <button
            className="export_btn"
            onClick={() => setShowExportOptions(!showExportOptions)}
          >
            Export
          </button>
          {showExportOptions && (
            <div className="export-options">
              <button
                onClick={() => {
                  handleExportPDF();
                  setShowExportOptions(false);
                }}
              >
                Export to PDF
              </button>
              <button
                onClick={() => {
                  handleExportExcel();
                  setShowExportOptions(false);
                }}
              >
                Export to Excel
              </button>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div className="faculty-table-container">
          <table id="attendance-table">
            <thead>
              <tr>
                <th className="text-nowrap">User Code</th>
                <th>Name</th>
                {[...Array(daysInMonth)].map((_, i) => (
                  <th key={i}>{i + 1}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((person, index) => (
                  <tr key={index}>
                    <td>{person.userCode || person.employeeCode}</td>
                    <td className="text-nowrap">{person.name}</td>
                    {Array.from({ length: daysInMonth }, (_, i) => (
                      <td
                        key={i}
                        className={`attendance-cell ${
                          person.attendance[i]?.toLowerCase() || ""
                        }`}
                      >
                        {renderAttendanceCell(person.attendance[i])}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={daysInMonth + 2} style={{ textAlign: "center" }}>
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminAttendanceHistory;
